import { Typography, Table, Tag, Select, Input, Button, Alert, message } from "antd";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { CloseOutlined, CopyOutlined, ReloadOutlined } from "@ant-design/icons";

const API = process.env.REACT_APP_BASE_URL;

const authHeaders = () => ({
  headers: { "Content-Type": "application/json", token: Cookies.get("token") },
});

/** Payout track — what Transferven owes the beneficiary. */
const PAYOUT_STATUSES = ["Pendiente", "Recibido", "En Proceso", "Completada", "Anulada"];

const statusColor = (s) => {
  const v = String(s || "").toUpperCase();
  if (v === "COMPLETADA") return "green";
  if (v === "RECIBIDO") return "blue";
  if (v === "EN PROCESO") return "gold";
  if (v === "ANULADA") return "volcano";
  return "default"; // Pendiente
};

/** Collection track — whether PayTKS actually took the money. */
const pmiColor = (s) => {
  if (["FILLED", "SENT", "COMPLETED"].includes(s)) return "green";
  if (["FAILED", "CANCELLED"].includes(s)) return "volcano";
  if (s === "INCIDENCE") return "red";
  return "default";
};

const fmtEs = (n, d = 2) =>
  Number(n || 0).toLocaleString("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Bank details get retyped into a banking portal — make them one click. */
const Copyable = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0" }}>
      <span style={{ color: "#667085", fontSize: 13 }}>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <b style={{ fontVariantNumeric: "tabular-nums", wordBreak: "break-all" }}>{value}</b>
        <CopyOutlined
          style={{ cursor: "pointer", color: "#98a2b3" }}
          onClick={() => {
            navigator.clipboard.writeText(String(value));
            message.success(`${label} copiado`);
          }}
        />
      </span>
    </div>
  );
};

const PmiTransactions = () => {
  const { Title } = Typography;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [awaitingPayout, setAwaitingPayout] = useState(0);

  const [filterStatus, setFilterStatus] = useState("");
  const [onlyPaid, setOnlyPaid] = useState(false);
  const [search, setSearch] = useState("");

  const [detail, setDetail] = useState(null);
  const [docs, setDocs] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [payoutRef, setPayoutRef] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  /* ---------------------------------------------------------------- */

  const FetchOrders = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ page, limit: 100 });
        if (filterStatus) params.set("status", filterStatus);
        if (onlyPaid) params.set("paid", "true");
        if (search.trim()) params.set("q", search.trim());

        const { data } = await axios.get(
          `${API}/api/pmi/admin/orders?${params.toString()}`,
          authHeaders()
        );
        setOrders(data.orders || []);
        setTotalOrders(data.total || 0);
        setCurrentPage(data.page || 1);
        setAwaitingPayout(data.awaitingPayout || 0);
      } catch (error) {
        console.log(error);
        message.error("No se pudieron cargar las órdenes");
      } finally {
        setLoading(false);
      }
    },
    [filterStatus, onlyPaid, search]
  );

  useEffect(() => {
    FetchOrders(1);
  }, [FetchOrders]);

  /** Sender identity + ID images, for verifying before paying out. */
  const openDetail = async (order) => {
    setDetail(order);
    setDocs(null);
    try {
      const { data } = await axios.get(
        `${API}/api/pmi/admin/order/${order.reference}/documents`,
        authHeaders()
      );
      setDocs(data);
    } catch (err) {
      console.warn("documents unavailable", err?.message);
    }
  };

  const HandleStatus = async (order, status) => {
    if (
      ["En Proceso", "Completada"].includes(status) &&
      !order.paid &&
      !window.confirm(
        "Esta orden NO tiene el pago confirmado por PayTKS. ¿Continuar de todas formas?"
      )
    )
      return;

    if (
      !window.confirm(
        `Cambiar ${order.reference} a "${status}"?\n\n` +
          `Se enviará un correo al cliente ${order.email}.`
      )
    )
      return;

    try {
      setSaving(true);
      const { data } = await axios.put(
        `${API}/api/pmi/admin/order/${order.reference}/status`,
        { status, payoutRef: payoutRef.trim() || undefined, note: note.trim() || undefined },
        authHeaders()
      );
      setOrders((prev) =>
        prev.map((o) => (o.reference === order.reference ? data.order : o))
      );
      if (detail?.reference === order.reference) setDetail(data.order);
      setStatusModal(null);
      setPayoutRef("");
      setNote("");
      message.success(data.msg);
      FetchOrders(currentPage);
    } catch (err) {
      message.error(err?.response?.data?.msg || "No se pudo cambiar el estado");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------- */

  const columns = [
    { title: "Referencia", dataIndex: "ref", key: "ref", width: 120 },
    { title: "Fecha", dataIndex: "date", key: "date", width: 130 },
    { title: "Cliente", dataIndex: "client", key: "client" },
    { title: "Beneficiario", dataIndex: "benef", key: "benef" },
    { title: "Envía", dataIndex: "sent", key: "sent", width: 100 },
    { title: "Recibe", dataIndex: "gets", key: "gets", width: 130 },
    {
      title: "Pago",
      dataIndex: "pmiStatus",
      key: "pmiStatus",
      width: 110,
      render: (v) => <Tag color={pmiColor(v)}>{v}</Tag>,
    },
    {
      title: "Envío",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (v) => <Tag color={statusColor(v)}>{String(v || "").toUpperCase()}</Tag>,
    },
    { title: "Acciones", dataIndex: "actions", key: "actions", width: 210 },
  ];

  const dataSource = orders.map((o) => ({
    key: o._id,
    ref: o.reference,
    date: fmtDate(o.createdAt),
    client: o.name || o.email,
    benef: o.beneficiary?.bname,
    sent: `€ ${fmtEs(o.gross)}`,
    gets: `${fmtEs(o.receives)} ${o.currencyTo}`,
    pmiStatus: o.pmiStatus,
    status: o.status,
    actions: (
      <>
        <Button
          type="primary"
          size="small"
          style={{ marginRight: 8 }}
          onClick={() => {
            setStatusModal(o);
            setPayoutRef(o.payoutRef || "");
            setNote("");
          }}
        >
          Estado
        </Button>
        <Button size="small" onClick={() => openDetail(o)}>
          Detalles
        </Button>
      </>
    ),
  }));

  /* ---------------------------------------------------------------- */

  return (
    <>
      <div>
        <Title level={window.matchMedia("(max-width: 600px)").matches ? 4 : 3}>
          Órdenes PMI
        </Title>

        {awaitingPayout > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${awaitingPayout} ${
              awaitingPayout === 1 ? "orden pagada pendiente" : "órdenes pagadas pendientes"
            } de envío a Venezuela`}
            description="El cliente ya ha pagado. Falta hacer la transferencia al beneficiario y marcar la orden como Completada."
            action={
              <Button
                size="small"
                onClick={() => {
                  setFilterStatus("Recibido");
                  setOnlyPaid(true);
                }}
              >
                Ver cola
              </Button>
            }
          />
        )}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <Select
            placeholder="Estado del envío"
            value={filterStatus || "ALL"}
            onChange={(v) => setFilterStatus(v === "ALL" ? "" : v)}
            style={{ width: 180 }}
          >
            <Select.Option value="ALL">TODOS</Select.Option>
            {PAYOUT_STATUSES.map((s) => (
              <Select.Option key={s} value={s}>
                {s.toUpperCase()}
              </Select.Option>
            ))}
          </Select>

          <Select
            value={onlyPaid ? "paid" : "any"}
            onChange={(v) => setOnlyPaid(v === "paid")}
            style={{ width: 180 }}
          >
            <Select.Option value="any">Pago: cualquiera</Select.Option>
            <Select.Option value="paid">Solo pagadas</Select.Option>
          </Select>

          <Input.Search
            placeholder="Referencia, email, cédula, cuenta…"
            allowClear
            style={{ width: 280 }}
            onSearch={(v) => setSearch(v)}
          />

          <Button icon={<ReloadOutlined />} onClick={() => FetchOrders(currentPage)}>
            Actualizar
          </Button>
        </div>

        <Table
          className="responsiveTable transactions"
          dataSource={dataSource}
          columns={columns}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: currentPage,
            total: totalOrders,
            pageSize: 100,
            showSizeChanger: false,
            onChange: (page) => FetchOrders(page),
          }}
        />
      </div>

      {/* ============ change status ============ */}
      {statusModal && (
        <div className="bg_status" id="bg_status">
          <style>{`
            /* .status_modal from the admin stylesheet stretches full width and
               has no padding — scope our own box instead of fighting it. */
            .tv-sm {
              width: 100%;
              max-width: 520px;
              background: #fff;
              border-radius: 14px;
              padding: 28px 30px 26px;
              position: relative;
              box-shadow: 0 20px 60px rgba(16,24,40,.24);
            }
            .tv-sm-close {
              position: absolute; top: 20px; right: 22px;
              font-size: 18px; color: #98a2b3; cursor: pointer;
              padding: 6px; border-radius: 6px; transition: background .15s;
            }
            .tv-sm-close:hover { background: #f2f4f7; color: #101828; }

            .tv-sm h3 {
              font-size: 1.15rem; font-weight: 700; margin: 0 0 6px;
              padding-right: 40px; letter-spacing: -.01em;
            }
            .tv-sm-meta {
              display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
              font-size: .88rem; color: #667085; margin-bottom: 22px;
              padding-bottom: 18px; border-bottom: 1px solid #eaecf0;
            }
            .tv-sm-meta b { color: #101828; font-variant-numeric: tabular-nums; }

            .tv-sm-label {
              display: block; font-size: .82rem; font-weight: 600;
              color: #475467; margin-bottom: 6px;
            }
            .tv-sm-field { margin-bottom: 16px; }
            .tv-sm .ant-input { border-radius: 9px; }
            .tv-sm .ant-input:not(textarea) { height: 44px; }

            .tv-sm-actions {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
              gap: 10px;
              margin-top: 22px;
              padding-top: 20px;
              border-top: 1px solid #eaecf0;
            }
            .tv-sm-actions .ant-btn {
              height: 44px; border-radius: 9px;
              font-weight: 600; font-size: .88rem;
            }
            .tv-sm-foot {
              font-size: .78rem; color: #98a2b3;
              margin: 16px 0 0; text-align: center;
            }
            @media (max-width: 560px) {
              .tv-sm { max-width: 92%; padding: 24px 20px 22px; }
              .tv-sm-actions { grid-template-columns: 1fr 1fr; }
            }
          `}</style>

          <div className="tv-sm">
            <CloseOutlined className="tv-sm-close" onClick={() => setStatusModal(null)} />

            <h3>
              {statusModal.reference} — cambiar estado
            </h3>

            <div className="tv-sm-meta">
              <span>
                Pago <Tag color={pmiColor(statusModal.pmiStatus)}>{statusModal.pmiStatus}</Tag>
              </span>
              <span>·</span>
              <span>
                A entregar <b>{fmtEs(statusModal.receives)} {statusModal.currencyTo}</b>
              </span>
              <span>·</span>
              <span>
                Cobrado <b>€ {fmtEs(statusModal.gross)}</b>
              </span>
            </div>

            <div className="tv-sm-field">
              <label className="tv-sm-label">Referencia del pago en Venezuela</label>
              <Input
                placeholder="Ej. 0134-00123456"
                value={payoutRef}
                onChange={(e) => setPayoutRef(e.target.value)}
              />
            </div>

            <div className="tv-sm-field" style={{ marginBottom: 0 }}>
              <label className="tv-sm-label">Nota interna / motivo</label>
              <Input.TextArea
                placeholder="Solo visible para el equipo, salvo en anulaciones"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
            </div>

            <div className="tv-sm-actions">
              {PAYOUT_STATUSES.map((s) => (
                <Button
                  key={s}
                  danger={s === "Anulada"}
                  type={s === "Completada" ? "primary" : "default"}
                  disabled={saving || statusModal.status === s}
                  onClick={() => HandleStatus(statusModal, s)}
                >
                  {s.toUpperCase()}
                </Button>
              ))}
            </div>

            <p className="tv-sm-foot">
              El cliente recibirá un correo con el nuevo estado.
            </p>
          </div>
        </div>
      )}

      {/* ============ detail ============ */}
      {detail && (
        <div className="modal_bg d-flex justify-content-center align-items-center">
          <style>{`
            /* The admin stylesheet gives .modal-body no height cap, so long
               orders overflow past the viewport with nothing to scroll.
               Make the body the scroll container and pin header/footer. */
            .tv-dm.modal-dialog {
              max-width: 680px !important;
              width: 100% !important;
              margin: 0 auto;
              max-height: 90vh;
            }
            .tv-dm .modal-content {
              max-height: 90vh;
              display: flex;
              flex-direction: column;
              border: none;
              border-radius: 14px;
              box-shadow: 0 20px 60px rgba(16,24,40,.24);
              overflow: hidden;
            }
            .tv-dm .modal-header,
            .tv-dm .modal-footer {
              flex: 0 0 auto;
              padding: 16px 22px;
            }
            .tv-dm .modal-header { border-bottom: 1px solid #eaecf0; }
            .tv-dm .modal-footer { border-top: 1px solid #eaecf0; gap: 10px; }
            .tv-dm .modal-body {
              flex: 1 1 auto;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
              padding: 20px 22px;
            }
            /* a visible but quiet scrollbar, so it's obvious there's more below */
            .tv-dm .modal-body::-webkit-scrollbar { width: 8px; }
            .tv-dm .modal-body::-webkit-scrollbar-thumb {
              background: #d0d5dd; border-radius: 4px;
            }
            .tv-dm .modal-body::-webkit-scrollbar-track { background: #f9fafb; }

            .tv-dm-title {
              display: flex; align-items: center; gap: 8px;
              flex-wrap: wrap; margin: 0; font-size: 1.05rem; font-weight: 700;
            }
            @media (max-width: 720px) {
              .tv-dm.modal-dialog { max-width: 94% !important; max-height: 92vh; }
              .tv-dm .modal-content { max-height: 92vh; }
            }
          `}</style>

          <div className="modal d-block" tabIndex="-1" style={{ position: "relative", background:  "rgba(0,0,0,0.3)"}}>
            <div className="modal-dialog tv-dm">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title tv-dm-title">
                    <span>{detail.reference}</span>
                    <Tag color={statusColor(detail.status)} style={{ margin: 0 }}>
                      {String(detail.status || "").toUpperCase()}
                    </Tag>
                    <Tag color={pmiColor(detail.pmiStatus)} style={{ margin: 0 }}>
                      {detail.pmiStatus}
                    </Tag>
                  </h1>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setDetail(null);
                      setDocs(null);
                    }}
                  />
                </div>

                <div className="modal-body">
                  {/* the operator's actual job */}
                  <div
                    style={{
                      background: "#f9fafb",
                      border: "1px solid #eaecf0",
                      borderRadius: 10,
                      padding: "14px 16px",
                      marginBottom: 18,
                    }}
                  >
                    <p style={{ fontWeight: 700, marginBottom: 8 }}>
                      Transferencia a realizar
                    </p>
                    <Copyable label="Beneficiario" value={detail.beneficiary?.bname} />
                    <Copyable label="Cédula" value={detail.beneficiary?.cedula} />
                    <Copyable label="Banco" value={detail.beneficiary?.bbank} />
                    <Copyable
                      label={
                        String(detail.beneficiary?.bAccnumber || "").length === 20
                          ? "Cuenta"
                          : "Pago móvil"
                      }
                      value={detail.beneficiary?.bAccnumber}
                    />
                    <Copyable
                      label="Importe a entregar"
                      value={`${fmtEs(detail.receives)} ${detail.currencyTo}`}
                    />
                    {detail.beneficiary?.Rphone && (
                      <Copyable label="Avisar a" value={detail.beneficiary.Rphone} />
                    )}
                  </div>

                  <p style={{ fontWeight: 700, marginBottom: 6 }}>Cobro</p>
                  <Copyable label="Cobrado al cliente" value={`€ ${fmtEs(detail.gross)}`} />
                  <Copyable label="Comisión" value={`€ ${fmtEs(detail.fee)}`} />
                  <Copyable
                    label="Tasa aplicada"
                    value={`1 EUR = ${fmtEs(detail.fxRate, 4)} ${detail.currencyTo}`}
                  />
                  <Copyable label="Método" value={detail.paymentMethod} />
                  <Copyable label="ID en PayTKS" value={detail.pmiOrderId} />
                  <Copyable label="Pagado el" value={fmtDate(detail.paidAt)} />

                  <p style={{ fontWeight: 700, margin: "18px 0 6px" }}>Remitente</p>
                  <Copyable label="Nombre" value={docs?.sender?.name || detail.name} />
                  <Copyable label="Email" value={detail.email} />
                  <Copyable label="Teléfono" value={docs?.sender?.phone} />
                  <Copyable label="Documento" value={docs?.docId} />
                  <Copyable label="Código cliente" value={docs?.sender?.uid} />

                  {docs && (docs.front || docs.currentFront) && (
                    <>
                      <p style={{ fontWeight: 700, margin: "18px 0 8px" }}>
                        Documento de identidad
                      </p>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {[
                          ["Anverso", docs.front || docs.currentFront],
                          ["Reverso", docs.back || docs.currentBack],
                        ]
                          .filter(([, url]) => url)
                          .map(([label, url]) => (
                            <a
                              key={label}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              style={{ textAlign: "center" }}
                            >
                              <img
                                src={url}
                                alt={label}
                                style={{
                                  width: 150,
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid #eaecf0",
                                }}
                              />
                              <div style={{ fontSize: 12, color: "#667085" }}>{label}</div>
                            </a>
                          ))}
                      </div>
                    </>
                  )}

                  {detail.payoutRef && (
                    <>
                      <p style={{ fontWeight: 700, margin: "18px 0 6px" }}>Pago realizado</p>
                      <Copyable label="Referencia" value={detail.payoutRef} />
                      <Copyable label="Fecha" value={fmtDate(detail.payoutAt)} />
                      <Copyable label="Por" value={detail.payoutBy} />
                    </>
                  )}
                  {detail.payoutNote && (
                    <p style={{ fontSize: 13, color: "#667085", marginTop: 8 }}>
                      Nota: <i>{detail.payoutNote}</i>
                    </p>
                  )}

                  {detail.statusHistory?.length > 0 && (
                    <>
                      <p style={{ fontWeight: 700, margin: "18px 0 6px" }}>Historial</p>
                      {[...detail.statusHistory].reverse().map((h, i) => (
                        <div
                          key={i}
                          style={{
                            fontSize: 13,
                            padding: "5px 0",
                            borderBottom: "1px solid #f2f4f7",
                          }}
                        >
                          <Tag color={h.track === "pmi" ? "default" : "blue"}>
                            {h.track === "pmi" ? "PAGO" : "ENVÍO"}
                          </Tag>
                          <b>{h.status}</b>{" "}
                          <span style={{ color: "#98a2b3" }}>
                            {fmtDate(h.at)}
                            {h.source ? ` · ${h.source}` : ""}
                            {h.by ? ` · ${h.by}` : ""}
                            {h.note ? ` · ${h.note}` : ""}
                          </span>
                        </div>
                      ))}
                    </>
                  )}

                  {detail.lastError && (
                    <Alert
                      type="error"
                      style={{ marginTop: 14 }}
                      message="Error al crear la orden en PayTKS"
                      description={detail.lastError}
                    />
                  )}
                </div>

                <div className="modal-footer">
                  <Button
                    type="primary"
                    onClick={() => {
                      setDetail(null);
                      setStatusModal(detail);
                      setPayoutRef(detail.payoutRef || "");
                      setNote("");
                    }}
                  >
                    Cambiar estado
                  </Button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setDetail(null);
                      setDocs(null);
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PmiTransactions;